import React, { useState, useEffect, useMemo } from "react";
import { useApp } from "@/context/AppContext";
import api from "@/services/api";
import { toast } from "sonner";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Plus,
  Pencil,
  Trash2,
  Search,
  Wrench,
  Target,
  Loader2,
  AlertCircle,
  X,
  Filter,
  FolderCog,
  Tag,
  Users,
  BarChart3,
  TrendingUp,
  AlertTriangle,
  CheckCircle,
  UserCheck,
  GraduationCap,
} from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

// Catégories prédéfinies par défaut (fallback)
const DEFAULT_CATEGORIES = [
  "Production",
  "Manutention",
  "Usinage",
  "Assemblage",
  "Contrôle",
  "Emballage",
  "Maintenance",
  "Autre",
];

export default function GestionMachinesCompetences() {
  const { currentUser } = useApp();
  const [machines, setMachines] = useState([]);
  const [competences, setCompetences] = useState([]);
  const [categories, setCategories] = useState([]); // Catégories/secteurs de machines
  const [grille, setGrille] = useState({});
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategorie, setSelectedCategorie] = useState("all");
  const [activeTab, setActiveTab] = useState("machines");
  
  // Dialog states
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedMachine, setSelectedMachine] = useState(null);
  const [formData, setFormData] = useState({
    nom: "",
    code: "",
    description: "",
    categorie: "",
    competences_requises: [],
    formations_requises: [],
  });
  const [saving, setSaving] = useState(false);
  const [typesFormations, setTypesFormations] = useState([]);
  
  // Category management states
  const [showAddCatDialog, setShowAddCatDialog] = useState(false);
  const [showEditCatDialog, setShowEditCatDialog] = useState(false);
  const [showDeleteCatDialog, setShowDeleteCatDialog] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [catFormData, setCatFormData] = useState({
    code: "",
    libelle: "",
    description: "",
    ordre: 0,
  });

  // Check permissions
  const canManage = ["Admin", "RH", "Manager", "HSE"].includes(currentUser?.role);
  const canDelete = currentUser?.role === "Admin";

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      // Add timestamp to stats request to prevent caching
      const timestamp = Date.now();
      const [machinesRes, competencesRes, grilleRes, categoriesRes, statsRes, typesFormationsRes] = await Promise.all([
        api.get("/api/machines-competences?actif=true"),
        api.get("/api/competences?actif=true"),
        api.get("/api/competences/grille"),
        api.get("/api/categories-machines?actif=true").catch(() => []),
        api.get(`/api/machines-competences/statistiques?_t=${timestamp}`).catch(() => null),
        api.get("/api/types-formations").catch(() => []),
      ]);
      
      setMachines(machinesRes || []);
      setCompetences(competencesRes || []);
      setGrille(grilleRes || {});
      setCategories(categoriesRes || []);
      setStats(statsRes);
      setTypesFormations(typesFormationsRes || []);
    } catch (error) {
      console.error("Error loading data:", error);
      toast.error("Erreur lors du chargement des données");
    } finally {
      setLoading(false);
    }
  };

  const filteredMachines = useMemo(() => {
    return machines.filter((m) => {
      const matchesSearch =
        m.nom?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        m.code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        m.description?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCategorie =
        selectedCategorie === "all" || m.categorie === selectedCategorie;
      return matchesSearch && matchesCategorie;
    });
  }, [machines, searchTerm, selectedCategorie]);

  const handleAdd = () => {
    setFormData({
      nom: "",
      code: "",
      description: "",
      categorie: "",
      competences_requises: [],
      formations_requises: [],
    });
    setShowAddDialog(true);
  };

  const handleEdit = (machine) => {
    setSelectedMachine(machine);
    setFormData({
      nom: machine.nom || "",
      code: machine.code || "",
      description: machine.description || "",
      categorie: machine.categorie || "",
      competences_requises: machine.competences_requises || [],
      formations_requises: machine.formations_requises || [],
    });
    setShowEditDialog(true);
  };

  const handleDelete = (machine) => {
    setSelectedMachine(machine);
    setShowDeleteDialog(true);
  };

  const handleAddCompetenceRequise = () => {
    setFormData({
      ...formData,
      competences_requises: [
        ...formData.competences_requises,
        { competence_id: "", niveau_minimum: 2 },
      ],
    });
  };

  const handleRemoveCompetenceRequise = (index) => {
    const updated = [...formData.competences_requises];
    updated.splice(index, 1);
    setFormData({ ...formData, competences_requises: updated });
  };

  const handleCompetenceChange = (index, field, value) => {
    const updated = [...formData.competences_requises];
    updated[index] = { ...updated[index], [field]: value };
    setFormData({ ...formData, competences_requises: updated });
  };

  const handleSaveNew = async () => {
    if (!formData.nom.trim()) {
      toast.error("Le nom de la machine est requis");
      return;
    }
    
    // Filter out empty competences
    const competencesRequises = formData.competences_requises.filter(
      c => c.competence_id
    );
    
    try {
      setSaving(true);
      await api.post("/api/machines-competences", {
        ...formData,
        competences_requises: competencesRequises,
        formations_requises: formData.formations_requises || [],
      });
      toast.success("Machine créée avec succès");
      setShowAddDialog(false);
      loadData();
    } catch (error) {
      console.error("Error creating machine:", error);
      toast.error(error.response?.data?.detail || "Erreur lors de la création");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveEdit = async () => {
    if (!formData.nom.trim()) {
      toast.error("Le nom de la machine est requis");
      return;
    }
    
    // Filter out empty competences
    const competencesRequises = formData.competences_requises.filter(
      c => c.competence_id
    );
    
    try {
      setSaving(true);
      await api.put(`/api/machines-competences/${selectedMachine.id}`, {
        ...formData,
        competences_requises: competencesRequises,
        formations_requises: formData.formations_requises || [],
      });
      toast.success("Machine modifiée avec succès");
      setShowEditDialog(false);
      loadData();
    } catch (error) {
      console.error("Error updating machine:", error);
      toast.error(error.response?.data?.detail || "Erreur lors de la modification");
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmDelete = async () => {
    try {
      setSaving(true);
      await api.delete(`/api/machines-competences/${selectedMachine.id}`);
      toast.success("Machine supprimée");
      setShowDeleteDialog(false);
      loadData();
    } catch (error) {
      console.error("Error deleting machine:", error);
      toast.error(error.response?.data?.detail || "Erreur lors de la suppression");
    } finally {
      setSaving(false);
    }
  };

  // ================== Category Management ==================
  const handleAddCategory = () => {
    setCatFormData({ code: "", libelle: "", description: "", ordre: categories.length });
    setShowAddCatDialog(true);
  };

  const handleEditCategory = (cat) => {
    setSelectedCategory(cat);
    setCatFormData({
      code: cat.code || "",
      libelle: cat.libelle || "",
      description: cat.description || "",
      ordre: cat.ordre || 0,
    });
    setShowEditCatDialog(true);
  };

  const handleDeleteCategory = (cat) => {
    setSelectedCategory(cat);
    setShowDeleteCatDialog(true);
  };

  const handleSaveNewCategory = async () => {
    if (!catFormData.code.trim() || !catFormData.libelle.trim()) {
      toast.error("Le code et le libellé sont requis");
      return;
    }
    try {
      setSaving(true);
      await api.post("/api/categories-machines", catFormData);
      toast.success("Secteur créé avec succès");
      setShowAddCatDialog(false);
      loadData();
    } catch (error) {
      console.error("Error creating category:", error);
      toast.error(error.response?.data?.detail || "Erreur lors de la création");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveEditCategory = async () => {
    if (!catFormData.libelle.trim()) {
      toast.error("Le libellé est requis");
      return;
    }
    try {
      setSaving(true);
      await api.put(`/api/categories-machines/${selectedCategory.id}`, {
        libelle: catFormData.libelle,
        description: catFormData.description,
        ordre: catFormData.ordre,
      });
      toast.success("Secteur modifié avec succès");
      setShowEditCatDialog(false);
      loadData();
    } catch (error) {
      console.error("Error updating category:", error);
      toast.error(error.response?.data?.detail || "Erreur lors de la modification");
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmDeleteCategory = async () => {
    try {
      setSaving(true);
      await api.delete(`/api/categories-machines/${selectedCategory.id}`);
      toast.success("Secteur supprimé");
      setShowDeleteCatDialog(false);
      loadData();
    } catch (error) {
      console.error("Error deleting category:", error);
      toast.error(error.response?.data?.detail || "Erreur lors de la suppression");
    } finally {
      setSaving(false);
    }
  };

  // Get all unique categories from configured + used in machines + defaults
  const allCategories = useMemo(() => {
    const configuredCodes = categories.map((c) => c.code);
    const fromMachines = machines.map((m) => m.categorie).filter(Boolean);
    // Combine: configured first, then any from machines not in configured, then defaults
    const allCodes = [...new Set([...configuredCodes, ...fromMachines])];
    // Add defaults if no categories configured
    if (allCodes.length === 0) {
      return DEFAULT_CATEGORIES;
    }
    return allCodes.sort();
  }, [machines, categories]);

  // Create a categories map for easy lookup (code -> libelle)
  const categoriesMap = useMemo(() => {
    const map = {};
    categories.forEach((c) => {
      map[c.code] = c.libelle;
    });
    return map;
  }, [categories]);

  // Get available competences (not already selected)
  const getAvailableCompetences = (currentIndex) => {
    const selectedIds = formData.competences_requises
      .filter((_, i) => i !== currentIndex)
      .map(c => c.competence_id);
    return competences.filter(c => !selectedIds.includes(c.id));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Toggle formation required
  const handleToggleFormation = (typeFormationId) => {
    const current = formData.formations_requises || [];
    if (current.includes(typeFormationId)) {
      setFormData({
        ...formData,
        formations_requises: current.filter(id => id !== typeFormationId)
      });
    } else {
      setFormData({
        ...formData,
        formations_requises: [...current, typeFormationId]
      });
    }
  };

  const renderCompetenceForm = () => (
    <div className="space-y-6">
      {/* Compétences requises */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <Label className="text-sm font-medium flex items-center gap-2">
            <Target className="h-4 w-4" />
            Compétences requises
          </Label>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleAddCompetenceRequise}
          >
            <Plus className="h-4 w-4 mr-1" />
            Ajouter
          </Button>
        </div>
        
        {formData.competences_requises.length === 0 ? (
          <p className="text-sm text-gray-500 text-center py-4 border rounded-lg border-dashed">
            Aucune compétence requise. Cliquez sur &quot;Ajouter&quot; pour en définir.
          </p>
        ) : (
          <div className="space-y-2 max-h-60 overflow-y-auto">
            {formData.competences_requises.map((req, index) => (
              <div
                key={index}
                className="flex items-center gap-2 p-3 border rounded-lg bg-gray-50"
              >
                <Select
                  value={req.competence_id}
                  onValueChange={(value) => handleCompetenceChange(index, "competence_id", value)}
                >
                  <SelectTrigger className="flex-1">
                    <SelectValue placeholder="Sélectionner une compétence" />
                  </SelectTrigger>
                  <SelectContent>
                    {getAvailableCompetences(index).map((comp) => (
                      <SelectItem key={comp.id} value={comp.id}>
                        {comp.nom} {comp.categorie && `(${comp.categorie})`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                <div className="flex items-center gap-1">
                  <span className="text-xs text-gray-500">Niveau min:</span>
                  <Select
                    value={String(req.niveau_minimum)}
                    onValueChange={(value) => handleCompetenceChange(index, "niveau_minimum", parseInt(value))}
                  >
                    <SelectTrigger className="w-20">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(grille).map(([niveau, info]) => (
                        <SelectItem key={niveau} value={niveau}>
                          <div className="flex items-center gap-2">
                            <div
                              className="w-4 h-4 rounded-full"
                              style={{ backgroundColor: info.couleur }}
                            />
                            {niveau}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => handleRemoveCompetenceRequise(index)}
                  className="text-red-500 hover:text-red-700"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Formations requises */}
      <div className="space-y-4">
        <Label className="text-sm font-medium flex items-center gap-2">
          <GraduationCap className="h-4 w-4" />
          Formations requises (valides)
        </Label>
        
        {typesFormations.length === 0 ? (
          <p className="text-sm text-gray-500 text-center py-4 border rounded-lg border-dashed">
            Aucun type de formation configuré.
          </p>
        ) : (
          <div className="grid grid-cols-1 gap-2 max-h-48 overflow-y-auto border rounded-lg p-3">
            {typesFormations.map((type) => {
              const isSelected = (formData.formations_requises || []).includes(type.id);
              const typeName = type.intitule || type.nom || type.code || "Formation";
              return (
                <div
                  key={type.id}
                  className={`flex items-center gap-3 p-2 rounded cursor-pointer hover:bg-gray-50 ${isSelected ? "bg-blue-50 border border-blue-200" : ""}`}
                  onClick={() => handleToggleFormation(type.id)}
                >
                  <Checkbox
                    checked={isSelected}
                    onCheckedChange={() => handleToggleFormation(type.id)}
                    id={`formation-${type.id}`}
                  />
                  <div className="flex-1">
                    <label htmlFor={`formation-${type.id}`} className="text-sm font-medium cursor-pointer">
                      {typeName}
                    </label>
                    {type.validite_mois && (
                      <p className="text-xs text-gray-500">Validité: {type.validite_mois} mois</p>
                    )}
                  </div>
                  {isSelected && (
                    <Badge variant="secondary" className="text-xs">
                      Requis
                    </Badge>
                  )}
                </div>
              );
            })}
          </div>
        )}
        
        {(formData.formations_requises || []).length > 0 && (
          <p className="text-sm text-blue-600">
            {formData.formations_requises.length} formation(s) requise(s)
          </p>
        )}
      </div>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="gestion-machines-competences-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Wrench className="h-7 w-7 text-primary" />
            Machines & Compétences Requises
          </h1>
          <p className="text-gray-600 mt-1">
            Configurez les compétences minimales requises pour chaque machine
          </p>
        </div>
        {canManage && (
          <Button onClick={handleAdd} data-testid="add-machine-btn">
            <Plus className="h-4 w-4 mr-2" />
            Nouvelle Machine
          </Button>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Machines</p>
                <p className="text-2xl font-bold">{machines.length}</p>
              </div>
              <Wrench className="h-8 w-8 text-primary opacity-80" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Avec Exigences</p>
                <p className="text-2xl font-bold">
                  {machines.filter(m => m.competences_requises?.length > 0).length}
                </p>
              </div>
              <Target className="h-8 w-8 text-blue-500 opacity-80" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Sans Opérateur</p>
                <p className="text-2xl font-bold text-red-600">
                  {stats?.resume?.nb_machines_sans_operateur || 0}
                </p>
              </div>
              <AlertTriangle className="h-8 w-8 text-red-500 opacity-80" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Moy. Opérateurs/Machine</p>
                <p className="text-2xl font-bold text-green-600">
                  {stats?.resume?.moyenne_operateurs_par_machine || 0}
                </p>
              </div>
              <Users className="h-8 w-8 text-green-500 opacity-80" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs for Machines, Secteurs and Statistics */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList>
          <TabsTrigger value="machines" className="gap-2">
            <Wrench className="h-4 w-4" />
            Machines ({machines.length})
          </TabsTrigger>
          <TabsTrigger value="statistiques" className="gap-2">
            <BarChart3 className="h-4 w-4" />
            Statistiques
          </TabsTrigger>
          <TabsTrigger value="categories" className="gap-2">
            <FolderCog className="h-4 w-4" />
            Secteurs ({categories.length})
          </TabsTrigger>
        </TabsList>

        {/* Machines Tab */}
        <TabsContent value="machines" className="space-y-4">
          {/* Filters */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Rechercher une machine..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-9"
                    data-testid="search-machine-input"
                  />
                </div>
                <Select value={selectedCategorie} onValueChange={setSelectedCategorie}>
                  <SelectTrigger className="w-full sm:w-48" data-testid="filter-categorie">
                    <Filter className="h-4 w-4 mr-2" />
                    <SelectValue placeholder="Secteur" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous les secteurs</SelectItem>
                    {categories.length > 0 ? (
                      categories.map((cat) => (
                        <SelectItem key={cat.code} value={cat.code}>
                          {cat.libelle}
                        </SelectItem>
                      ))
                    ) : (
                      allCategories.map((cat) => (
                        <SelectItem key={cat} value={cat}>
                          {cat}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Table */}
          <Card>
            <CardHeader>
              <CardTitle>Machines ({filteredMachines.length})</CardTitle>
            </CardHeader>
            <CardContent>
              {filteredMachines.length === 0 ? (
                <div className="text-center py-12">
                  <Wrench className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-500">Aucune machine trouvée</p>
                  {canManage && (
                    <Button variant="outline" className="mt-4" onClick={handleAdd}>
                      <Plus className="h-4 w-4 mr-2" />
                      Ajouter une machine
                    </Button>
                  )}
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Code</TableHead>
                        <TableHead>Nom</TableHead>
                        <TableHead>Secteur</TableHead>
                        <TableHead>Compétences Requises</TableHead>
                        {canManage && <TableHead className="text-right">Actions</TableHead>}
                      </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredMachines.map((machine) => (
                    <TableRow key={machine.id} data-testid={`machine-row-${machine.id}`}>
                      <TableCell className="font-mono text-sm">
                        {machine.code || "-"}
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium">{machine.nom}</p>
                          {machine.description && (
                            <p className="text-sm text-gray-500 truncate max-w-xs">
                              {machine.description}
                            </p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        {machine.categorie ? (
                          <Badge variant="outline">
                            {categoriesMap[machine.categorie] || machine.categorie}
                          </Badge>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {machine.competences_requises?.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {machine.competences_requises.slice(0, 3).map((req, i) => (
                              <Badge
                                key={i}
                                variant="secondary"
                                className="text-xs"
                              >
                                {req.competence_nom} (≥{req.niveau_minimum})
                              </Badge>
                            ))}
                            {machine.competences_requises.length > 3 && (
                              <Badge variant="outline" className="text-xs">
                                +{machine.competences_requises.length - 3}
                              </Badge>
                            )}
                          </div>
                        ) : (
                          <span className="text-gray-400 text-sm">Aucune</span>
                        )}
                      </TableCell>
                      {canManage && (
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEdit(machine)}
                              data-testid={`edit-machine-${machine.id}`}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            {canDelete && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDelete(machine)}
                                className="text-red-500 hover:text-red-700"
                                data-testid={`delete-machine-${machine.id}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
        </TabsContent>

        {/* Statistics Tab */}
        <TabsContent value="statistiques" className="space-y-4">
          {/* Stats Summary */}
          {stats && (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <Card className="border-l-4 border-l-blue-500">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-500">Taux de Couverture</p>
                        <p className="text-2xl font-bold text-blue-600">{stats.resume?.taux_couverture || 0}%</p>
                        <p className="text-xs text-gray-400 mt-1">Machines avec au moins 1 opérateur</p>
                      </div>
                      <TrendingUp className="h-8 w-8 text-blue-500 opacity-80" />
                    </div>
                  </CardContent>
                </Card>
                <Card className="border-l-4 border-l-green-500">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-500">Total Collaborateurs</p>
                        <p className="text-2xl font-bold text-green-600">{stats.resume?.nb_collaborateurs || 0}</p>
                        <p className="text-xs text-gray-400 mt-1">Actifs dans le système</p>
                      </div>
                      <Users className="h-8 w-8 text-green-500 opacity-80" />
                    </div>
                  </CardContent>
                </Card>
                <Card className="border-l-4 border-l-orange-500">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-500">Machines Critiques</p>
                        <p className="text-2xl font-bold text-orange-600">{stats.resume?.nb_machines_critiques || 0}</p>
                        <p className="text-xs text-gray-400 mt-1">Moins de 2 opérateurs</p>
                      </div>
                      <AlertTriangle className="h-8 w-8 text-orange-500 opacity-80" />
                    </div>
                  </CardContent>
                </Card>
                <Card className="border-l-4 border-l-purple-500">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-500">Moy. Opérateurs</p>
                        <p className="text-2xl font-bold text-purple-600">{stats.resume?.moyenne_operateurs_par_machine || 0}</p>
                        <p className="text-xs text-gray-400 mt-1">Par machine</p>
                      </div>
                      <UserCheck className="h-8 w-8 text-purple-500 opacity-80" />
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Machines critiques */}
              {stats.machines_critiques?.length > 0 && (
                <Card className="border-red-200">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg flex items-center gap-2 text-red-600">
                      <AlertTriangle className="h-5 w-5" />
                      Machines à Surveiller
                    </CardTitle>
                    <CardDescription>
                      Ces machines ont moins de 2 opérateurs autorisés - risque de continuité
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Machine</TableHead>
                            <TableHead>Secteur</TableHead>
                            <TableHead>Compétences Requises</TableHead>
                            <TableHead>Opérateurs Autorisés</TableHead>
                            <TableHead>Statut</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {stats.machines_critiques.map((machine) => (
                            <TableRow key={machine.machine_id}>
                              <TableCell>
                                <div>
                                  <p className="font-medium">{machine.machine_nom}</p>
                                  {machine.machine_code && (
                                    <p className="text-xs text-gray-500">{machine.machine_code}</p>
                                  )}
                                </div>
                              </TableCell>
                              <TableCell>
                                {machine.categorie ? (
                                  <Badge variant="outline">{machine.categorie}</Badge>
                                ) : (
                                  <span className="text-gray-400">-</span>
                                )}
                              </TableCell>
                              <TableCell>
                                <Badge variant="secondary">{machine.nb_competences_requises}</Badge>
                              </TableCell>
                              <TableCell>
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger>
                                      <Badge 
                                        variant={machine.nb_operateurs_autorises === 0 ? "destructive" : "outline"}
                                        className={machine.nb_operateurs_autorises === 0 ? "" : "border-orange-500 text-orange-600"}
                                      >
                                        <Users className="h-3 w-3 mr-1" />
                                        {machine.nb_operateurs_autorises}
                                      </Badge>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      {machine.operateurs?.length > 0 ? (
                                        <div className="text-sm">
                                          {machine.operateurs.map((op, i) => (
                                            <p key={i}>{op.prenom} {op.nom}</p>
                                          ))}
                                        </div>
                                      ) : (
                                        <p>Aucun opérateur autorisé</p>
                                      )}
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              </TableCell>
                              <TableCell>
                                {machine.statut === "critique" ? (
                                  <Badge variant="destructive">Critique</Badge>
                                ) : (
                                  <Badge className="bg-orange-100 text-orange-800">Attention</Badge>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Stats par secteur */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <BarChart3 className="h-5 w-5" />
                    Statistiques par Secteur
                  </CardTitle>
                  <CardDescription>
                    Répartition des machines et opérateurs par secteur
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {stats.par_secteur?.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      Aucune donnée par secteur disponible
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {stats.par_secteur?.map((secteur) => (
                        <div key={secteur.secteur} className="p-4 border rounded-lg hover:shadow-sm transition-shadow">
                          <div className="flex justify-between items-center mb-2">
                            <div className="flex items-center gap-2">
                              <Badge variant="outline" className="font-medium">
                                {categoriesMap[secteur.secteur] || secteur.secteur}
                              </Badge>
                              <span className="text-sm text-gray-500">
                                {secteur.nb_machines} machine{secteur.nb_machines !== 1 ? "s" : ""}
                              </span>
                            </div>
                            <div className="flex items-center gap-4">
                              <div className="text-right">
                                <p className="text-sm font-medium">{secteur.nb_total_operateurs}</p>
                                <p className="text-xs text-gray-400">opérateurs total</p>
                              </div>
                              <div className="text-right">
                                <p className="text-sm font-medium text-blue-600">{secteur.moyenne_operateurs}</p>
                                <p className="text-xs text-gray-400">moy./machine</p>
                              </div>
                            </div>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div 
                              className="bg-blue-600 h-2 rounded-full transition-all"
                              style={{ 
                                width: `${Math.min(100, (secteur.moyenne_operateurs / 10) * 100)}%` 
                              }}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Top machines */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                    Top 5 - Machines les Plus Couvertes
                  </CardTitle>
                  <CardDescription>
                    Machines avec le plus grand nombre d&apos;opérateurs autorisés
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {stats.top_machines?.map((machine, index) => (
                      <div key={machine.machine_id} className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg">
                        <div className="w-8 h-8 rounded-full bg-green-100 text-green-700 flex items-center justify-center font-bold">
                          {index + 1}
                        </div>
                        <div className="flex-1">
                          <p className="font-medium">{machine.machine_nom}</p>
                          <div className="flex items-center gap-2 mt-1">
                            {machine.machine_code && (
                              <span className="text-xs text-gray-500">{machine.machine_code}</span>
                            )}
                            {machine.categorie && (
                              <Badge variant="outline" className="text-xs">{machine.categorie}</Badge>
                            )}
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-bold text-green-600">{machine.nb_operateurs_autorises}</p>
                          <p className="text-xs text-gray-400">opérateurs</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Détail par machine */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Users className="h-5 w-5" />
                    Opérateurs par Machine
                  </CardTitle>
                  <CardDescription>
                    Détail du nombre d&apos;opérateurs autorisés pour chaque machine
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Machine</TableHead>
                          <TableHead>Secteur</TableHead>
                          <TableHead>Compétences Requises</TableHead>
                          <TableHead>Opérateurs Autorisés</TableHead>
                          <TableHead>Statut</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {stats.machines?.map((machine) => (
                          <TableRow key={machine.machine_id}>
                            <TableCell>
                              <div>
                                <p className="font-medium">{machine.machine_nom}</p>
                                {machine.machine_code && (
                                  <p className="text-xs text-gray-500">{machine.machine_code}</p>
                                )}
                              </div>
                            </TableCell>
                            <TableCell>
                              {machine.categorie ? (
                                <Badge variant="outline">{machine.categorie}</Badge>
                              ) : (
                                <span className="text-gray-400">-</span>
                              )}
                            </TableCell>
                            <TableCell>
                              <Badge variant="secondary">{machine.nb_competences_requises}</Badge>
                            </TableCell>
                            <TableCell>
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger>
                                    <Badge 
                                      variant={machine.nb_operateurs_autorises === 0 ? "destructive" : "secondary"}
                                      className={machine.nb_operateurs_autorises >= 3 ? "bg-green-100 text-green-800" : ""}
                                    >
                                      <Users className="h-3 w-3 mr-1" />
                                      {machine.nb_operateurs_autorises}
                                    </Badge>
                                  </TooltipTrigger>
                                  <TooltipContent className="max-w-xs">
                                    {machine.operateurs?.length > 0 ? (
                                      <div className="text-sm">
                                        <p className="font-medium mb-1">Opérateurs autorisés:</p>
                                        {machine.operateurs.slice(0, 5).map((op, i) => (
                                          <p key={i}>• {op.prenom} {op.nom} ({op.matricule})</p>
                                        ))}
                                        {machine.total_operateurs > 5 && (
                                          <p className="text-gray-400 mt-1">
                                            +{machine.total_operateurs - 5} autres...
                                          </p>
                                        )}
                                      </div>
                                    ) : (
                                      <p>Aucun opérateur autorisé</p>
                                    )}
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            </TableCell>
                            <TableCell>
                              {machine.statut === "critique" ? (
                                <Badge variant="destructive">Critique</Badge>
                              ) : machine.statut === "attention" ? (
                                <Badge className="bg-orange-100 text-orange-800">Attention</Badge>
                              ) : (
                                <Badge className="bg-green-100 text-green-800">OK</Badge>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            </>
          )}
          
          {!stats && (
            <Card>
              <CardContent className="py-12 text-center">
                <BarChart3 className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                <p className="text-gray-500">Statistiques non disponibles</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Categories Tab */}
        <TabsContent value="categories" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <FolderCog className="h-5 w-5" />
                  Secteurs de Machines
                </CardTitle>
                <CardDescription>
                  Gérez les secteurs utilisés pour classer les machines
                </CardDescription>
              </div>
              {canManage && (
                <Button onClick={handleAddCategory} data-testid="add-category-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  Nouveau Secteur
                </Button>
              )}
            </CardHeader>
            <CardContent>
              {categories.length === 0 ? (
                <div className="text-center py-12">
                  <FolderCog className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-500">Aucun secteur configuré</p>
                  <p className="text-sm text-gray-400 mt-2">
                    Les secteurs par défaut seront utilisés jusqu&apos;à ce que vous en créiez.
                  </p>
                  {canManage && (
                    <Button variant="outline" className="mt-4" onClick={handleAddCategory}>
                      <Plus className="h-4 w-4 mr-2" />
                      Créer un secteur
                    </Button>
                  )}
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Code</TableHead>
                        <TableHead>Libellé</TableHead>
                        <TableHead>Description</TableHead>
                        <TableHead>Machines</TableHead>
                        {canManage && <TableHead className="text-right">Actions</TableHead>}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {categories.map((cat) => {
                        const machineCount = machines.filter(m => m.categorie === cat.code).length;
                        return (
                          <TableRow key={cat.id} data-testid={`category-row-${cat.id}`}>
                            <TableCell>
                              <Badge variant="outline" className="font-mono">
                                {cat.code}
                              </Badge>
                            </TableCell>
                            <TableCell className="font-medium">{cat.libelle}</TableCell>
                            <TableCell className="text-gray-500 text-sm max-w-xs truncate">
                              {cat.description || "-"}
                            </TableCell>
                            <TableCell>
                              <Badge variant={machineCount > 0 ? "secondary" : "outline"}>
                                {machineCount} machine{machineCount !== 1 ? "s" : ""}
                              </Badge>
                            </TableCell>
                            {canManage && (
                              <TableCell className="text-right">
                                <div className="flex justify-end gap-2">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleEditCategory(cat)}
                                    data-testid={`edit-category-${cat.id}`}
                                  >
                                    <Pencil className="h-4 w-4" />
                                  </Button>
                                  {canDelete && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => handleDeleteCategory(cat)}
                                      className="text-red-500 hover:text-red-700"
                                      disabled={machineCount > 0}
                                      title={machineCount > 0 ? "Supprimez d'abord les machines de ce secteur" : "Supprimer"}
                                      data-testid={`delete-category-${cat.id}`}
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  )}
                                </div>
                              </TableCell>
                            )}
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Nouvelle Machine</DialogTitle>
            <DialogDescription>
              Définissez une machine et ses compétences requises
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="nom">Nom *</Label>
                <Input
                  id="nom"
                  value={formData.nom}
                  onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                  placeholder="Ex: Presse hydraulique"
                  data-testid="machine-nom-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="code">Code</Label>
                <Input
                  id="code"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                  placeholder="Ex: PH-001"
                  data-testid="machine-code-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="categorie">Secteur</Label>
              <Select
                value={formData.categorie}
                onValueChange={(value) => setFormData({ ...formData, categorie: value })}
              >
                <SelectTrigger data-testid="machine-categorie-select">
                  <SelectValue placeholder="Sélectionner un secteur" />
                </SelectTrigger>
                <SelectContent>
                  {categories.length > 0 ? (
                    categories.map((cat) => (
                      <SelectItem key={cat.code} value={cat.code}>
                        {cat.libelle}
                      </SelectItem>
                    ))
                  ) : (
                    allCategories.map((cat) => (
                      <SelectItem key={cat} value={cat}>
                        {cat}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Description de la machine..."
                rows={2}
                data-testid="machine-description-input"
              />
            </div>
            {renderCompetenceForm()}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>
              Annuler
            </Button>
            <Button onClick={handleSaveNew} disabled={saving} data-testid="save-new-machine-btn">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Créer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Modifier la Machine</DialogTitle>
            <DialogDescription>Modifiez les informations et compétences requises</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-nom">Nom *</Label>
                <Input
                  id="edit-nom"
                  value={formData.nom}
                  onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                  data-testid="edit-machine-nom-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-code">Code</Label>
                <Input
                  id="edit-code"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                  data-testid="edit-machine-code-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-categorie">Secteur</Label>
              <Select
                value={formData.categorie}
                onValueChange={(value) => setFormData({ ...formData, categorie: value })}
              >
                <SelectTrigger data-testid="edit-machine-categorie-select">
                  <SelectValue placeholder="Sélectionner un secteur" />
                </SelectTrigger>
                <SelectContent>
                  {categories.length > 0 ? (
                    categories.map((cat) => (
                      <SelectItem key={cat.code} value={cat.code}>
                        {cat.libelle}
                      </SelectItem>
                    ))
                  ) : (
                    allCategories.map((cat) => (
                      <SelectItem key={cat} value={cat}>
                        {cat}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-description">Description</Label>
              <Textarea
                id="edit-description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={2}
                data-testid="edit-machine-description-input"
              />
            </div>
            {renderCompetenceForm()}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              Annuler
            </Button>
            <Button onClick={handleSaveEdit} disabled={saving} data-testid="save-edit-machine-btn">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertCircle className="h-5 w-5" />
              Supprimer la Machine
            </DialogTitle>
            <DialogDescription>
              Êtes-vous sûr de vouloir supprimer la machine &quot;{selectedMachine?.nom}&quot; ?
              Cette action est irréversible.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Annuler
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmDelete}
              disabled={saving}
              data-testid="confirm-delete-machine-btn"
            >
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Supprimer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Category Dialog */}
      <Dialog open={showAddCatDialog} onOpenChange={setShowAddCatDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Nouveau Secteur de Machine</DialogTitle>
            <DialogDescription>
              Créez un secteur pour organiser vos machines
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="cat-code">Code *</Label>
              <Input
                id="cat-code"
                value={catFormData.code}
                onChange={(e) => setCatFormData({ ...catFormData, code: e.target.value.toUpperCase() })}
                placeholder="Ex: MANU, PROD, CTRL"
                maxLength={20}
                data-testid="category-code-input"
              />
              <p className="text-xs text-gray-500">Code court unique pour identifier le secteur</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="cat-libelle">Libellé *</Label>
              <Input
                id="cat-libelle"
                value={catFormData.libelle}
                onChange={(e) => setCatFormData({ ...catFormData, libelle: e.target.value })}
                placeholder="Ex: Manutention, Production"
                data-testid="category-libelle-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cat-description">Description</Label>
              <Textarea
                id="cat-description"
                value={catFormData.description}
                onChange={(e) => setCatFormData({ ...catFormData, description: e.target.value })}
                placeholder="Description optionnelle..."
                rows={2}
                data-testid="category-description-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddCatDialog(false)}>
              Annuler
            </Button>
            <Button onClick={handleSaveNewCategory} disabled={saving} data-testid="save-new-category-btn">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Créer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Category Dialog */}
      <Dialog open={showEditCatDialog} onOpenChange={setShowEditCatDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Modifier le Secteur</DialogTitle>
            <DialogDescription>
              Modifiez les informations du secteur &quot;{selectedCategory?.code}&quot;
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Code</Label>
              <Input value={catFormData.code} disabled className="bg-gray-50" />
              <p className="text-xs text-gray-500">Le code ne peut pas être modifié</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-cat-libelle">Libellé *</Label>
              <Input
                id="edit-cat-libelle"
                value={catFormData.libelle}
                onChange={(e) => setCatFormData({ ...catFormData, libelle: e.target.value })}
                data-testid="edit-category-libelle-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-cat-description">Description</Label>
              <Textarea
                id="edit-cat-description"
                value={catFormData.description}
                onChange={(e) => setCatFormData({ ...catFormData, description: e.target.value })}
                rows={2}
                data-testid="edit-category-description-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditCatDialog(false)}>
              Annuler
            </Button>
            <Button onClick={handleSaveEditCategory} disabled={saving} data-testid="save-edit-category-btn">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Category Confirmation Dialog */}
      <Dialog open={showDeleteCatDialog} onOpenChange={setShowDeleteCatDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertCircle className="h-5 w-5" />
              Supprimer le Secteur
            </DialogTitle>
            <DialogDescription>
              Êtes-vous sûr de vouloir supprimer le secteur &quot;{selectedCategory?.libelle}&quot; ({selectedCategory?.code}) ?
              Cette action est irréversible.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteCatDialog(false)}>
              Annuler
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmDeleteCategory}
              disabled={saving}
              data-testid="confirm-delete-category-btn"
            >
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Supprimer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
